import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, FileText, Shield, Users, CreditCard, AlertTriangle, Scale, Clock, Mail } from 'lucide-react';

const TermsOfService = () => {
  const sections = [
    {
      icon: Users,
      title: "1. Acceptation des conditions",
      content: `En accédant à la plateforme DOCKHTA ou en l'utilisant, vous acceptez d'être lié par les présentes Conditions Générales d'Utilisation (CGU). Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser nos services.

Ces CGU régissent votre accès et votre utilisation de la plateforme DOCKHTA, y compris toutes les fonctionnalités, contenus, et services proposés.`
    },
    {
      icon: FileText,
      title: "2. Description des services",
      content: `DOCKHTA est une plateforme de santé numérique qui propose les services suivants :

• Recherche et prise de rendez-vous avec des professionnels de santé
• Téléconsultations médicales par vidéo
• Demande de soins à domicile
• Stockage sécurisé de documents médicaux (ordonnances, résultats d'examens)
• Assistant santé propulsé par intelligence artificielle
• Gestion du carnet de santé familial
• Annuaire des établissements de santé et pharmacies

DOCKHTA agit en tant qu'intermédiaire technologique et ne fournit pas directement de soins médicaux.`
    },
    {
      icon: Shield,
      title: "3. Inscription et compte utilisateur",
      content: `Pour utiliser certains services, vous devez créer un compte en fournissant des informations exactes et complètes.

Vous êtes responsable de :
• Maintenir la confidentialité de vos identifiants de connexion
• Toute activité effectuée depuis votre compte
• Mettre à jour vos informations personnelles

DOCKHTA se réserve le droit de suspendre ou fermer tout compte en cas de :
• Informations fausses ou trompeuses
• Violation des présentes CGU
• Activités frauduleuses ou illégales`
    },
    {
      icon: CreditCard,
      title: "4. Tarification et paiement",
      content: `L'utilisation de base de DOCKHTA est gratuite pour les patients.

Les consultations et services médicaux sont facturés selon les tarifs fixés par les professionnels de santé. Les prix affichés incluent toutes les taxes applicables.

Modes de paiement acceptés :
• Mobile Money (Orange Money, MTN MoMo)
• Carte bancaire
• Paiement sur place (pour certains services)

Les annulations sont possibles jusqu'à 24 heures avant le rendez-vous pour un remboursement intégral.`
    },
    {
      icon: AlertTriangle,
      title: "5. Responsabilités et limitations",
      content: `DOCKHTA ne peut être tenu responsable :
• Des conseils médicaux fournis par les professionnels de santé
• Des retards ou annulations de rendez-vous
• De l'indisponibilité temporaire de la plateforme
• Des pertes de données dues à des circonstances hors de notre contrôle

Les informations fournies par l'assistant IA sont à titre informatif uniquement et ne remplacent pas un avis médical professionnel.

En cas de situation d'urgence médicale, veuillez contacter immédiatement les services d'urgence (SAMU, pompiers) ou vous rendre aux urgences les plus proches.`
    },
    {
      icon: Scale,
      title: "6. Propriété intellectuelle",
      content: `Tous les contenus présents sur DOCKHTA (textes, images, logos, logiciels, bases de données) sont protégés par les droits de propriété intellectuelle.

Il est interdit de :
• Reproduire, modifier ou distribuer le contenu sans autorisation
• Utiliser la marque DOCKHTA sans accord préalable
• Extraire ou réutiliser les données de la plateforme

Les professionnels de santé conservent la propriété de leurs informations professionnelles et contenus publiés.`
    },
    {
      icon: Clock,
      title: "7. Modification des CGU",
      content: `DOCKHTA se réserve le droit de modifier les présentes CGU à tout moment.

En cas de modification substantielle, les utilisateurs seront informés par :
• Notification dans l'application
• Email à l'adresse enregistrée

La poursuite de l'utilisation de la plateforme après notification vaut acceptation des nouvelles conditions.`
    },
    {
      icon: Mail,
      title: "8. Contact et réclamations",
      content: `Pour toute question ou réclamation concernant ces CGU :

Email : legal@dockhta.com
Adresse : Yaoundé, Cameroun
Téléphone : +237 621 05 53 65

Nous nous engageons à répondre dans un délai de 72 heures ouvrées.`
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour à l&apos;accueil
            </Button>
          </Link>
          <Link to="/" className="text-2xl font-bold text-[#E53935]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            DOCKHTA
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="w-16 h-16 bg-[#E53935]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <FileText className="w-8 h-8 text-[#E53935]" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Conditions Générales d&apos;Utilisation
          </h1>
          <p className="text-slate-600">
            Dernière mise à jour : Juin 2026
          </p>
        </div>

        <div className="space-y-6">
          {sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="p-6 md:p-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#E53935]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#E53935]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900 mb-3">
                        {section.title}
                      </h2>
                      <div className="text-slate-600 whitespace-pre-line leading-relaxed">
                        {section.content}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer info */}
        <div className="mt-12 text-center text-slate-500 text-sm">
          <p>
            En utilisant DOCKHTA, vous confirmez avoir lu et accepté ces Conditions Générales d&apos;Utilisation.
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <Link to="/privacy" className="text-[#E53935] hover:underline">
              Politique de Confidentialité
            </Link>
            <span>•</span>
            <Link to="/mentions-legales" className="text-[#E53935] hover:underline">
              Mentions Légales
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsOfService;
